import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, CheckIcon, ChevronRightIcon, CopyIcon, MailIcon } from 'lucide-react';
import { CONTACT_EMAIL, contactTopics } from '../data/contact';
import { useSeo } from '../hooks/useSeo';
import { breadcrumbLd, organizationLd } from '../utils/seo';
import { trackEvent } from '../utils/analytics';
export function Contact() {
  const [copied, setCopied] = useState(false);
  useSeo({
    title: 'Contact | SlotGids – Online slots in Nieuw-Zeeland',
    description: `Neem contact op met SlotGids via ${CONTACT_EMAIL} voor samenwerkingen, correcties, persvragen of vragen over de Top 30 online slots in Nieuw-Zeeland.`,
    path: '/contact',
    jsonLd: [breadcrumbLd([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Contact',
      path: '/contact'
    }]), {
      ...organizationLd(),
      '@type': 'Organization'
    }]
  });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      trackEvent('contact_copy_email');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return <main className="mx-auto max-w-7xl px-4 pt-10 sm:px-6" data-id="element-366" data-mp="1e78nnrxdg00g">
      <nav aria-label="Kruimelpad" className="flex items-center gap-1 text-sm text-muted" data-id="element-367" data-mp="1e9ts8op20u1w">
        <Link to="/" className="hover:text-ink" data-id="element-368" data-mp="1j621jlgklt15">Home</Link>
        <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-369" data-mp="mhcm01sf1cic" />
        <span className="text-ink" aria-current="page" data-id="element-370" data-mp="qi6aoxdomvca">Contact</span>
      </nav>

      <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20" data-id="element-371" data-mp="qk8z5cz6sgew">
        <div data-id="element-372" data-mp="vfoany9q2085">
          <h1 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl" data-id="element-373" data-mp="vjc2msziqekr">Contact</h1>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-muted" data-id="element-374" data-mp="10e985pxla5sw">
            Vragen, ideeën of een samenwerking? Stuur ons een e-mail. We antwoorden binnen twee werkdagen, in het
            Nederlands of Engels.
          </p>

          <div className="mt-8 rounded-2xl bg-ink p-6 text-white" data-id="element-375" data-mp="1m6u4os6k2bgn">
            <p className="text-sm text-white/70" data-id="element-376" data-mp="1m96l30wo0drl">E-mail</p>
            <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => trackEvent('contact_click', {
            topic: 'algemeen'
          })} className="mt-1 block break-all font-serif text-2xl font-semibold underline-offset-4 hover:underline sm:text-3xl" data-id="element-377" data-mp="1h7h6xce5bjza">
              {CONTACT_EMAIL}
            </a>
            <div className="mt-5 flex flex-wrap gap-2" data-id="element-378" data-mp="13erkx7ymaf26">
              <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => trackEvent('contact_click', {
              topic: 'algemeen'
            })} className="inline-flex h-11 items-center gap-2 rounded-lg bg-oranje px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-oranje-dark" data-id="element-379" data-mp="yiu470jfz6ov">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-380" data-mp="ko9uxb565xki" />
                Stuur een e-mail
              </a>
              <button type="button" onClick={copy} className="inline-flex h-11 items-center gap-2 rounded-lg border border-white/25 px-5 text-sm font-semibold transition-colors duration-150 hover:bg-white/10" data-id="element-381" data-mp="2remyo2xkc2sh">
                {copied ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-382" data-mp="2hedzqhc6s39e" /> : <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-383" data-mp="2mglfli0503h0" />}
                <span aria-live="polite" data-id="element-384" data-mp="3a5e3cr6lv50j">{copied ? 'Gekopieerd' : 'Kopieer adres'}</span>
              </button>
            </div>
          </div>

          <p className="mt-6 max-w-md text-sm leading-relaxed text-muted" data-id="element-385" data-mp="3f2wgyuvki8ev">
            SlotGids is geen casino en heeft geen toegang tot spelersaccounts. Voor vragen over stortingen of
            uitbetalingen neem je contact op met het casino zelf. Hulp nodig bij gokproblemen? Bel de Gambling Helpline
            op{' '}
            <a href="tel:0800654655" className="font-medium text-ink underline underline-offset-2" data-id="element-386" data-mp="1prds44fyjkag">0800 654 655</a>.
          </p>
        </div>

        <section aria-labelledby="topics-title" data-id="element-387" data-mp="29yj35y6opccd">
          <h2 id="topics-title" className="font-serif text-2xl font-semibold" data-id="element-388" data-mp="29zc067fucnhb">Waarover wil je contact opnemen?</h2>
          <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-389" data-mp="255hitanpkkle">
            {contactTopics.map(t => <li key={t.title} data-id="element-390" data-mp="1v9ehg70okpzi">
                <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t.subject)}`} onClick={() => trackEvent('contact_click', {
              topic: t.subject
            })} className="group flex items-start justify-between gap-6 py-5" data-id="element-391" data-mp="1va61ckkx0z9y">
                  <div data-id="element-392" data-mp="2ixny6uj1enjs">
                    <p className="font-semibold group-hover:underline" data-id="element-393" data-mp="2ix4fvcph5x58">{t.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted" data-id="element-394" data-mp="2ewc4odx1li6b">{t.description}</p>
                  </div>
                  <ArrowUpRightIcon className="mt-1 h-5 w-5 shrink-0 text-muted transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink" aria-hidden="true" data-id="element-395" data-mp="17kv9jsljmf7y" />
                </a>
              </li>)}
          </ul>
        </section>
      </div>
    </main>;
}